/**
 * Ownership Service
 * Handles entity ownership operations for content-creators
 * Allows sharing, revoking, and managing access to content
 */

import api from './apiClient';

/**
 * @typedef {Object} OwnerUser
 * @property {string} id - User ID
 * @property {string} firstName - User's first name
 * @property {string} lastName - User's last name
 * @property {string} email - User's email
 * @property {string} displayName - User's display name
 */

/**
 * @typedef {Object} SharedOwner
 * @property {OwnerUser} user - The user who has shared access
 * @property {OwnerUser} sharedBy - The user who granted the access
 * @property {string} dateShared - When access was granted
 */

/**
 * @typedef {Object} EntityOwnership
 * @property {string} entityType - Type of the entity
 * @property {number} entityId - ID of the entity
 * @property {OwnerUser|null} mainOwner - The main owner (null if unowned)
 * @property {SharedOwner[]} sharedOwners - List of users with shared access
 * @property {string} accessLevel - Current user's access level: 'none', 'sharedOwner', 'mainOwner', 'adminOverride'
 * @property {boolean} canEdit - Whether current user can edit
 * @property {boolean} canDelete - Whether current user can delete
 * @property {boolean} canManageOwnership - Whether current user can share/revoke
 */

/**
 * @typedef {Object} AvailableUser
 * @property {string} id - User ID
 * @property {string} firstName - User's first name
 * @property {string} lastName - User's last name
 * @property {string} email - User's email
 * @property {string} displayName - User's display name
 */

/**
 * @typedef {Object} AvailableUsersResult
 * @property {AvailableUser[]} users - List of available users
 * @property {number} totalCount - Total number of available users
 */

/**
 * Get ownership information for an entity
 * @param {string} entityType - Type of entity (e.g., 'FileEntity', 'Course')
 * @param {number} entityId - ID of the entity
 * @param {Object} [options] - Request options
 * @returns {Promise<EntityOwnership>}
 */
export const getOwnershipInfo = async (entityType, entityId, options = {}) => {
  return api.get(`/ownership/${entityType}/${entityId}`, {}, options);
};

/**
 * Share ownership of an entity with another content-creator
 * @param {string} entityType - Type of entity
 * @param {number} entityId - ID of the entity
 * @param {string} userId - ID of the user to share with
 * @param {Object} [options] - Request options
 * @returns {Promise<{message: string}>}
 */
export const shareOwnership = async (entityType, entityId, userId, options = {}) => {
  return api.post(`/ownership/${entityType}/${entityId}/share`, { userId }, options);
};

/**
 * Revoke shared ownership from a user
 * @param {string} entityType - Type of entity
 * @param {number} entityId - ID of the entity
 * @param {string} userId - ID of the user to revoke from
 * @param {Object} [options] - Request options
 * @returns {Promise<{message: string}>}
 */
export const revokeOwnership = async (entityType, entityId, userId, options = {}) => {
  return api.delete(`/ownership/${entityType}/${entityId}/share/${userId}`, {}, options);
};

/**
 * Transfer main ownership to another content-creator (admin only)
 * @param {string} entityType - Type of entity
 * @param {number} entityId - ID of the entity
 * @param {string} newOwnerId - ID of the new owner
 * @param {Object} [options] - Request options
 * @returns {Promise<{message: string}>}
 */
export const transferOwnership = async (entityType, entityId, newOwnerId, options = {}) => {
  return api.post(`/ownership/${entityType}/${entityId}/transfer`, { newOwnerId }, options);
};

/**
 * Get list of content-creator users available to share with
 * @param {string} entityType - Type of entity
 * @param {number} entityId - ID of the entity
 * @param {string} [search] - Optional search term
 * @param {Object} [options] - Request options
 * @returns {Promise<AvailableUsersResult>}
 */
export const getAvailableUsersToShare = async (entityType, entityId, search = null, options = {}) => {
  const params = search ? { search } : {};
  return api.get(`/ownership/${entityType}/${entityId}/available-users`, params, options);
};

/**
 * Check if current user can edit an entity
 * @param {string} entityType - Type of entity
 * @param {number} entityId - ID of the entity
 * @param {Object} [options] - Request options
 * @returns {Promise<{canEdit: boolean}>}
 */
export const canEdit = async (entityType, entityId, options = {}) => {
  return api.get(`/ownership/${entityType}/${entityId}/can-edit`, {}, options);
};

/**
 * Check if current user can delete an entity
 * @param {string} entityType - Type of entity
 * @param {number} entityId - ID of the entity
 * @param {Object} [options] - Request options
 * @returns {Promise<{canDelete: boolean}>}
 */
export const canDelete = async (entityType, entityId, options = {}) => {
  return api.get(`/ownership/${entityType}/${entityId}/can-delete`, {}, options);
};

/**
 * Access level constants
 */
export const AccessLevel = {
  NONE: 'none',
  SHARED_OWNER: 'sharedowner',
  MAIN_OWNER: 'mainowner',
  ADMIN_OVERRIDE: 'adminoverride',
};

/**
 * Check if an access level grants edit/delete permissions
 * @param {string} accessLevel - The access level to check
 * @returns {boolean}
 */
export const hasEditAccess = (accessLevel) => {
  const level = accessLevel?.toLowerCase();
  return level === AccessLevel.MAIN_OWNER || 
         level === AccessLevel.SHARED_OWNER || 
         level === AccessLevel.ADMIN_OVERRIDE;
};

/**
 * Check if an access level allows managing ownership (share/revoke)
 * @param {string} accessLevel - The access level to check
 * @returns {boolean}
 */
export const hasManageAccess = (accessLevel) => {
  const level = accessLevel?.toLowerCase();
  return level === AccessLevel.MAIN_OWNER || 
         level === AccessLevel.ADMIN_OVERRIDE;
};

// Default export with all methods
export default {
  getOwnershipInfo,
  shareOwnership,
  revokeOwnership,
  transferOwnership,
  getAvailableUsersToShare,
  canEdit,
  canDelete,
  AccessLevel,
  hasEditAccess,
  hasManageAccess,
};
